import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchProducts } from "../lib/api";
import { ProductCard } from "../components/ProductCard";

const HERO_VIDEO = "/generated/hero-video.mp4";
const HERO_VIDEO_WEBM = "/generated/hero-video.webm";
const MOOD_1 = "https://customer-assets.emergentagent.com/job_9f46cec6-d33c-4d03-bbf8-7fcace4b7e00/artifacts/6ocbr3dx_Screenshot%202026-04-26%20at%2015.40.49.png";
const MOOD_2 = "https://customer-assets.emergentagent.com/job_9e6ba60a-229b-427a-9303-6a3b518a6683/artifacts/8ac5qind_Lake-Como-Industry-Insights-main-jpg-1536x884.webp";
const MOOD_3 = "https://customer-assets.emergentagent.com/job_9e6ba60a-229b-427a-9303-6a3b518a6683/artifacts/zfvagasu_faraglioni.jpg";
const MOOD_4 = "https://customer-assets.emergentagent.com/job_9e6ba60a-229b-427a-9303-6a3b518a6683/artifacts/725l0rp9_f827e9e20f73ec168e8cb915068a8f1a25fc0b2a-2000x922.png.avif";

export default function Home() {
  const [products, setProducts] = useState([]);
  const videoRef = useRef(null);

  useEffect(() => {
    fetchProducts().then((all) => setProducts(all.filter((p) => p.category !== "care"))).catch(console.error);
  }, []);

  // Keep the hero video playing despite browser autoplay throttling / buffering stalls.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const tryPlay = () => {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };

    // Resume whenever the browser pauses/stalls it without user intent.
    const onPause = () => { if (!v.ended) tryPlay(); };
    const onStall = () => tryPlay();
    const onVisibility = () => { if (!document.hidden) tryPlay(); };
    const onInteract = () => tryPlay();

    v.addEventListener("pause", onPause);
    v.addEventListener("stalled", onStall);
    v.addEventListener("waiting", onStall);
    v.addEventListener("canplay", onStall);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("scroll", onInteract, { passive: true });
    window.addEventListener("touchstart", onInteract, { passive: true });
    window.addEventListener("click", onInteract);

    tryPlay();

    return () => {
      v.removeEventListener("pause", onPause);
      v.removeEventListener("stalled", onStall);
      v.removeEventListener("waiting", onStall);
      v.removeEventListener("canplay", onStall);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("scroll", onInteract);
      window.removeEventListener("touchstart", onInteract);
      window.removeEventListener("click", onInteract);
    };
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[100vh] min-h-[640px] -mt-20 flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            data-testid="hero-video"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
            <source src={HERO_VIDEO_WEBM} type="video/webm" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/40" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 pb-20 text-white w-full">
          <p className="eyebrow text-white/80 fade-up">Колекція 2026 · Riviera</p>
          <h1 className="font-serif-display fade-up-delay-1 text-5xl sm:text-7xl lg:text-8xl leading-[1.05] mt-4 max-w-3xl">
            <span className="tw-line tw-1">Будь власною</span><br />
            <span className="tw-line tw-2 italic-sing font-light text-silea-yellow">музою.</span>
          </h1>
          <p className="mt-6 max-w-lg text-white/85 fade-up-delay-2 text-base sm:text-lg leading-relaxed">
            Купальники з італійської тканини, створені вручну в Україні.
            Для жінок, які пишуть свою власну поезію літа.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-6 fade-up-delay-3">
            <Link
              to="/products"
              data-testid="hero-shop-cta"
              className="inline-flex items-center gap-3 bg-silea-yellow text-silea-navy px-8 py-4 eyebrow hover:bg-white transition-colors duration-500"
            >
              До колекції <ArrowRight size={16} strokeWidth={1.6} />
            </Link>
            <Link to="/about" className="silea-link eyebrow text-white" data-testid="hero-about-link">
              Історія бренду
            </Link>
          </div>
        </div>
      </section>

      {/* MOODBOARD STRIP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="moodboard-section">
        <div className="grid grid-cols-12 gap-6 sm:gap-8">
          <div className="col-span-12 lg:col-span-5 flex flex-col justify-center">
            <p className="eyebrow text-silea-navy/60">Італійська естетика</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-4 leading-[1.1] text-silea-navy">
              Амальфі. Лимони.<br />
              <span className="italic-sing">Безкінечне літо.</span>
            </h2>
            <p className="mt-6 text-silea-navy/70 leading-relaxed max-w-md">
              Кожна модель SILÉA — це лист з італійського узбережжя. Білі стіни, теракотові дахи, лимонні дерева та море кольору сапфіру. Ми переплавили це у тканину, яка пам'ятає сонце.
            </p>
            <p className="font-script text-3xl text-silea-navy/70 mt-8">— from Amalfi, with love</p>
          </div>
          <div className="col-span-6 lg:col-span-4 aspect-[3/4] overflow-hidden relative paper-grain tilt-l shadow-lg">
            <img src={MOOD_1} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
          <div className="col-span-6 lg:col-span-3 aspect-[3/4] overflow-hidden lg:mt-20 relative paper-grain tilt-rr shadow-lg">
            <img src={MOOD_3} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
          <div className="col-span-12 lg:col-span-7 aspect-[16/9] overflow-hidden relative paper-grain tilt-ll shadow-lg">
            <img src={MOOD_2} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
          <div className="hidden lg:block col-span-5 aspect-[16/9] overflow-hidden relative paper-grain tilt-r shadow-lg">
            <img src={MOOD_4} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS — Editorial lookbook layout */}
      <section className="bg-white border-y border-stone-200" data-testid="featured-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-16">
            <div>
              <p className="eyebrow text-silea-navy/60">Колекція 2026</p>
              <h2 className="font-serif-display text-4xl sm:text-6xl mt-3 text-silea-navy leading-[1.05]">
                Обрані <span className="italic-sing">музи</span>
              </h2>
            </div>
            <Link to="/products" className="silea-link eyebrow text-silea-navy" data-testid="featured-view-all">
              Дивитись усі моделі →
            </Link>
          </div>

          {products.length >= 3 && (
            <div className="grid grid-cols-12 gap-x-6 gap-y-16 lg:gap-y-24">
              {/* 01 — large left, lifted */}
              <div className="col-span-12 lg:col-span-7 lg:row-span-2 relative">
                <span className="absolute -top-6 -left-2 font-serif-display text-silea-yellow/80 text-7xl italic-sing pointer-events-none select-none">01</span>
                <ProductCard product={products[0]} />
              </div>
              {/* 02 — smaller right, dropped down */}
              <div className="col-span-12 sm:col-span-6 lg:col-span-5 lg:mt-24 relative">
                <span className="absolute -top-6 -left-2 font-serif-display text-silea-yellow/80 text-7xl italic-sing pointer-events-none select-none">02</span>
                <ProductCard product={products[1]} />
              </div>
              {/* 03 — bottom right */}
              <div className="col-span-12 sm:col-span-6 lg:col-span-5 lg:col-start-8 lg:-mt-12 relative">
                <span className="absolute -top-6 -left-2 font-serif-display text-silea-yellow/80 text-7xl italic-sing pointer-events-none select-none">03</span>
                <ProductCard product={products[2]} />
              </div>
            </div>
          )}

          {/* fallback grid for additional products */}
          {products.length > 3 && (
            <div className="mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
              {products.slice(3, 6).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* BRAND STORY */}
      <section className="bg-silea-light-blue/30" data-testid="brand-story-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="eyebrow text-silea-navy/60">Філософія</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-3 leading-tight text-silea-navy">
              Італійська душа.<br />
              <span className="italic-sing">Українські руки.</span>
            </h2>
            <p className="mt-6 text-silea-navy/80 leading-relaxed">
              SILÉA народилася на перетині двох світів. Італія подарувала нам відчуття краси, dolce vita і тканини найвищої якості. Україна — характер, точність ручної роботи та сміливість бути собою.
            </p>
            <p className="mt-4 text-silea-navy/80 leading-relaxed">
              Кожен купальник проходить понад 40 операцій. Декор з кристалів Swarovski, морських камінців і перлин — це наша підпис, ваш ритуал.
            </p>
            <Link to="/about" className="inline-block mt-8 silea-link eyebrow text-silea-navy" data-testid="story-read-more">
              Більше про SILÉA →
            </Link>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 grid grid-cols-2 gap-4">
            <div className="aspect-[3/4] overflow-hidden">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/9f46cec6-d33c-4d03-bbf8-7fcace4b7e00/images/61e09ef39fd7159ee4abd2f31a35021e8bc2ad4d4ef5eed102613d84ef0c4ac1.png"
                alt="Swarovski"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover img-hover"
              />
            </div>
            <div className="aspect-[3/4] overflow-hidden mt-12">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/9f46cec6-d33c-4d03-bbf8-7fcace4b7e00/images/35c186bbd32eb4bf9145fec8553d9b8617b185e1262902b6f4da5796731c2c2c.png"
                alt="Crystal lemon"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover img-hover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CUSTOMIZATION TEASER — Split + 3 steps */}
      <section className="bg-silea-cream" data-testid="custom-teaser-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* LEFT: two sketches, asymmetric */}
          <div className="lg:col-span-6">
            <div className="grid grid-cols-12 gap-3 sm:gap-5">
              <div className="col-span-7 aspect-[3/4] overflow-hidden">
                <img
                  src="/generated/sketch-swimsuit.webp"
                  alt="Ескіз авторського купальника SILÉA — коричневий"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover img-hover"
                />
              </div>
              <div className="col-span-5 aspect-[3/4] overflow-hidden mt-16 sm:mt-24">
                <img
                  src="/generated/sketch-swimsuit-2.webp"
                  alt="Ескіз авторського купальника SILÉA — жовтий"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover img-hover"
                />
              </div>
            </div>
            <div className="mt-8 sm:ml-12 lg:ml-20 bg-silea-yellow text-silea-navy px-6 py-5 max-w-sm inline-block">
              <p className="font-serif-display italic text-lg leading-snug">
                «Кожен кристал — крапля сонця, що пам'ятає Амальфі.»
              </p>
            </div>
          </div>

          {/* RIGHT: heading + 3 steps + CTA */}
          <div className="lg:col-span-6 lg:pl-4">
            <p className="eyebrow text-silea-navy/60">Made for you</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] text-silea-navy">
              Зробіть купальник <span className="italic-sing text-silea-yellow">унікальним</span>.
            </h2>
            <p className="mt-6 text-silea-navy/70 leading-relaxed max-w-md">
              Кристали Swarovski, морські камінці, перлини, ваше ім'я або авторський мотив — лимон, інкрустований кристалами.
            </p>

            <ol className="mt-12 space-y-8" data-testid="custom-steps">
              {[
                { n: "01", title: "Оберіть модель", desc: "Amalfi, Capri або Ravello — ваша основа." },
                { n: "02", title: "Додайте декор", desc: "Кристали, перлини, мотиви або ваше ім'я." },
                { n: "03", title: "Отримайте за 7–10 днів", desc: "Вшиваємо вручну. Доставка Новою Поштою." },
              ].map((s) => (
                <li key={s.n} className="flex gap-5 group">
                  <span className="font-serif-display text-3xl text-silea-yellow leading-none w-12 shrink-0">
                    {s.n}
                  </span>
                  <div className="border-l border-silea-navy/15 pl-5 group-hover:border-silea-yellow transition-colors">
                    <p className="eyebrow text-silea-navy">{s.title}</p>
                    <p className="text-sm text-silea-navy/60 mt-2 leading-relaxed">{s.desc}</p>
                  </div>
                </li>
              ))}
            </ol>

            <Link
              to="/customizer"
              data-testid="custom-cta"
              className="inline-flex items-center gap-3 mt-12 bg-silea-navy text-white px-8 py-4 eyebrow hover:bg-silea-yellow hover:text-silea-navy transition-colors duration-500"
            >
              Створити свій купальник <ArrowRight size={16} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
